import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {
  getFileConfigVariables,
  SYSTEM_GENERATED_CONFIG_VARIABLE_KEYS_BY_STEP_TYPE,
} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Metadata and compatibility conditions for an integration with analysis templates.
 *
 * @package
 */
export interface AnalysisTemplateIntegration {
  name: string;
  icon: BenchlingIconName;
  visible: () => boolean;
  checks: Array<CompatibilityCheckFn>;
}

export enum CheckResultLevel {
  PASS = 'PASS',
  FAIL = 'FAIL',
  WARN = 'WARN',
}

/** @package */
export interface CompatibilityCheckResult {
  level: CheckResultLevel;
  message: string;
}

/**
 * Check a compatibility condition.
 *
 * PASS/FAIL conditions should state the same positive requirement, e.g.: 'Must have no table variables'.
 *
 * WARNINGS should appear if applicable, and should explain the situation with a complete sentence.
 *
 * @returns CompatibilityCheckResult if a message should be shown, else null.
 * @package
 */
export type CompatibilityCheckFn = (variables: TemplateVariablesByType) => CompatibilityCheckResult | null;

/**
 * Basic variable types.
 *
 * `AnalysisTemplateVariable` models are discriminated into `FILE`, `FIELD` and `OUTPUT`, for display purposes.
 *
 * @package
 */
export enum VariableType {
  /**
   * An `AnalysisTemplateDataFrameVariable`
   */
  TABLE = 'TABLE',
  /**
   * An `AnalysisTemplateVariable` which is a `domain` type of `FILE`
   */
  FILE = 'FILE',
  /**
   * An `AnalysisTemplateVariable` from a `SEND_TO_DOCUMENT` or `EXPORT_DATA_CATALOG_DATASET` step.
   *
   * Some integrations which start from a document support only these `AnalysisTemplateVariable` variables,
   * and set the values implicitly based on the document's context (e.g. in which folder the entry is).
   */
  OUTPUT = 'OUTPUT',
  /**
   * Any other `AnalysisTemplateVariable`. A value a user enters into a field.
   */
  FIELD = 'FIELD',
}

/**
 * User-facing name of the basic variable types.
 *
 * @package
 */
export const VARIABLE_TYPE_TO_NAME: Record<VariableType, string> = {
  [VariableType.TABLE]: 'source table',
  [VariableType.FILE]: 'source file',
  [VariableType.FIELD]: 'template variable',
  [VariableType.OUTPUT]: 'Export table as dataset or Send to document step',
};

/**
 * A template's variables grouped by their basic types.
 *
 * @package
 */
export interface TemplateVariablesByType {
  [VariableType.TABLE]: AnalysisTemplateGraphQL['dataFrameVariables'];
  [VariableType.FILE]: AnalysisTemplateGraphQL['configVariables'];
  [VariableType.FIELD]: AnalysisTemplateGraphQL['configVariables'];
  [VariableType.OUTPUT]: AnalysisTemplateGraphQL['configVariables'];
}

/**
 * These variables are generated by SEND_TO_DOCUMENT and EXPORT_DATA_CATALOG_DATASET, and can be set
 * implicitly when executing within an entry or worksheet context.
 */
const SEND_OUTPUT_VARIABLE_KEYS: ReadonlySet<string> = new Set([
  ...SYSTEM_GENERATED_CONFIG_VARIABLE_KEYS_BY_STEP_TYPE[PipelineStepType.EXPORT_DATA_CATALOG_DATASET],
  ...SYSTEM_GENERATED_CONFIG_VARIABLE_KEYS_BY_STEP_TYPE[PipelineStepType.SEND_TO_DOCUMENT],
]);

/**
 * Groups a template's variables by their type.
 *
 * @package
 */
export function getTemplateVariablesByType(
  template: Pick<AnalysisTemplateGraphQL, 'dataFrameVariables' | 'configVariables'>
): TemplateVariablesByType {
  const fileVariables = getFileConfigVariables(template.configVariables);
  const outputVariables = template.configVariables.filter((configVariable) =>
    SEND_OUTPUT_VARIABLE_KEYS.has(configVariable.configVariableKey)
  );

  return {
    [VariableType.TABLE]: template.dataFrameVariables,
    [VariableType.FILE]: fileVariables,
    [VariableType.FIELD]: template.configVariables.filter(
      (configVariable) =>
        !fileVariables.includes(configVariable) &&
        !SEND_OUTPUT_VARIABLE_KEYS.has(configVariable.configVariableKey)
    ),
    [VariableType.OUTPUT]: outputVariables,
  };
}
